<template>
        <div v-if="isWebTarget" ref="menuElement" class="reader-user-menu" :class="{ open: isOpen, authenticated: sessionUser }">
                <Button type="button" class="trigger" variant="text" severity="secondary" aria-label="Аккаунт" aria-haspopup="dialog" :aria-expanded="isOpen" :aria-controls="isOpen ? 'reader-user-hint' : undefined" @click="toggleHint">
                        <span class="icon" aria-hidden="true"><span v-if="sessionUser" class="account-avatar" :class="`account-avatar--${sessionUser.avatarId}`"></span></span>
                </Button>

                <section v-if="isOpen" id="reader-user-hint" class="hint" role="dialog" aria-labelledby="reader-user-hint-title">
                        <div class="body">
                                <div class="account">
                                        <span class="portrait" aria-hidden="true"><span v-if="sessionUser" class="account-avatar" :class="`account-avatar--${sessionUser.avatarId}`"></span></span>
                                        <div class="details">
                                                <div id="reader-user-hint-title" class="title">{{ sessionUser ? sessionUser.email : 'Аккаунт не подключён' }}</div>
                                                <template v-if="!sessionUser">
                                                        <p class="message">Войдите в аккаунт, чтобы использовать дополнительные возможности</p>
                                                        <Button type="button" class="login" label="ВОЙТИ В АККАУНТ" icon="pi pi-angle-right" icon-pos="right" @click="signIn" />
                                                </template>
                                        </div>
                                </div>
                                <div v-if="sessionUser" class="account-actions">
                                        <Button type="button" class="account-action profile" variant="text" severity="secondary" label="Профиль" icon="pi pi-cog" @click="showProfile" />
                                        <Button type="button" class="account-action" variant="text" severity="secondary" label="Справка" icon="pi pi-question-circle" @click="openHelp" />
                                        <Button type="button" class="account-action" variant="text" severity="secondary" :label="isLoggingOut ? 'Выход…' : 'Выйти из аккаунта'" icon="pi pi-sign-out" :disabled="isLoggingOut" @click="signOut" />
                                </div>
                                <div v-else class="help-row">
                                        <Button type="button" class="account-action" variant="text" severity="secondary" label="Справка" icon="pi pi-question-circle" @click="openHelp" />
                                </div>
                                <p v-if="actionMessage || sessionError" class="account-message" role="status">{{ actionMessage || sessionError }}</p>
                        </div>
                </section>
                <ReaderAccountProfile v-model:visible="isProfileOpen" />
        </div>
        <div v-else class="avatar" aria-label="Заглушка аватара пользователя" role="img"></div>
</template>

<script setup lang="ts">
        import "@/less/account-avatars.less"
        import "@/less/user-menu.less"
        import { nextTick, onBeforeUnmount, onMounted, ref } from "vue"
        import { useRoute, useRouter } from "vue-router"
        import { logoutSession, refreshSession, sessionError, sessionUser } from "@/ts/api/auth-session"
        import ReaderAccountProfile from "@/window/ReaderAccountProfile.vue"

        const router = useRouter()
        const route = useRoute()
        const isWebTarget = import.meta.env.VITE_EBOOK_TARGET === "web"
        const menuElement = ref<HTMLElement | null>(null)
        const isOpen = ref(false)
        const isLoggingOut = ref(false)
        const actionMessage = ref('')
        const isProfileOpen = ref(false)

        const toggleHint = async () => {
                isOpen.value = !isOpen.value
                if (isOpen.value) {
                        actionMessage.value = ''
                        void refreshSession()
                        await nextTick()
                        menuElement.value?.querySelector<HTMLButtonElement>(sessionUser.value ? ".profile" : ".login")?.focus({ preventScroll: true })
                }
        }

        const signIn = () => {
                isOpen.value = false
                void router.push({ name: "authorization", state: { authReturnPath: route.fullPath } })
        }

        const openHelp = () => {
                actionMessage.value = 'Справка пока не подключена.'
        }

        const showProfile = () => {
                isOpen.value = false
                isProfileOpen.value = true
        }

        const signOut = async () => {
                if (isLoggingOut.value) return
                isLoggingOut.value = true
                actionMessage.value = ''
                try {
                        await logoutSession()
                        isOpen.value = false
                        await nextTick()
                        menuElement.value?.querySelector<HTMLButtonElement>(".trigger")?.focus({ preventScroll: true })
                } catch (error) {
                        actionMessage.value = error instanceof Error ? error.message : 'Не удалось выйти из аккаунта.'
                } finally {
                        isLoggingOut.value = false
                }
        }

        const handleVisibility = () => {
                if (document.visibilityState === 'visible') void refreshSession()
        }

        const handleOutsidePointer = (event: PointerEvent) => {
                if (isOpen.value && event.target instanceof Node && !menuElement.value?.contains(event.target)) {
                        isOpen.value = false
                }
        }

        const handleEscape = (event: KeyboardEvent) => {
                if (isOpen.value && event.key === "Escape") {
                        event.preventDefault()
                        event.stopPropagation()
                        isOpen.value = false
                        menuElement.value?.querySelector<HTMLButtonElement>(".trigger")?.focus({ preventScroll: true })
                }
        }

        onMounted(() => {
                if (!isWebTarget) return
                void refreshSession()
                document.addEventListener("pointerdown", handleOutsidePointer)
                document.addEventListener("keydown", handleEscape)
                document.addEventListener("visibilitychange", handleVisibility)
                window.addEventListener("focus", handleVisibility)
        })

        onBeforeUnmount(() => {
                document.removeEventListener("pointerdown", handleOutsidePointer)
                document.removeEventListener("keydown", handleEscape)
                document.removeEventListener("visibilitychange", handleVisibility)
                window.removeEventListener("focus", handleVisibility)
        })
</script>
